/**
 * Период ленты: «Сегодня», «Вчера», неделя, месяц, всё время или диапазон из календаря.
 *
 * Период — это местные сутки целиком, обе границы включительно: диспетчер выбирает
 * «с 28 октября по 2 ноября», а не моменты времени. В запрос уходит полуинтервал
 * `[начало первых суток, начало суток после последних)` в UTC (ADR-0025, Р-020).
 */

export type PeriodPreset = 'today' | 'yesterday' | 'week' | 'month' | 'all' | 'last7';

export const PERIOD_PRESETS: readonly PeriodPreset[] = [
  'today',
  'yesterday',
  'week',
  'month',
  'all',
];

/** Быстрые периоды журнала аудита: «последние 7 дней» вместо календарной недели. */
export const AUDIT_PRESETS: readonly PeriodPreset[] = ['today', 'yesterday', 'last7'];

const WEEK_DAYS = 7;

/** Диапазон суток; `null` — всё время. */
export interface Period {
  readonly from: Date;
  readonly to: Date;
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/** Сдвиг на сутки календарём, а не на 24 часа: так переход на летнее время не ломает границы. */
export function addDays(value: Date, days: number): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Сутки для адреса страницы: `2026-11-02`. */
export function dayKey(value: Date): string {
  const pad = (part: number): string => String(part).padStart(2, '0');

  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

export function parseDay(value: string | null): Date | null {
  const match = value === null ? null : DAY_PATTERN.exec(value);

  if (match === null) {
    return null;
  }

  const [, year, month, day] = match.map(Number);
  const parsed = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);

  // `2026-02-31` Date молча превращает в 3 марта — такой адрес считается испорченным.
  return parsed.getDate() === day ? parsed : null;
}

/** Неделя — с понедельника по сегодня, месяц — с первого числа по сегодня. */
export function presetPeriod(preset: PeriodPreset, now: Date): Period | null {
  const today = startOfDay(now);

  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const yesterday = addDays(today, -1);

      return { from: yesterday, to: yesterday };
    }
    case 'week':
      return { from: addDays(today, -((today.getDay() + 6) % 7)), to: today };
    case 'month':
      return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
    case 'last7':
      return { from: addDays(today, 1 - WEEK_DAYS), to: today };
    case 'all':
      return null;
  }
}

/** Какой пресет совпадает с периодом; `null` — произвольный диапазон из календаря. */
export function presetOf(
  period: Period | null,
  now: Date,
  presets: readonly PeriodPreset[] = PERIOD_PRESETS,
): PeriodPreset | null {
  for (const preset of presets) {
    const candidate = presetPeriod(preset, now);

    if (
      candidate === null
        ? period === null
        : period !== null &&
          sameDay(candidate.from, period.from) &&
          sameDay(candidate.to, period.to)
    ) {
      return preset;
    }
  }

  return null;
}

/**
 * Период из адреса: `period=all` — всё время, `from`/`to` — диапазон, без параметров —
 * сегодня, как в панели. Испорченный адрес тоже даёт «сегодня», а не пустую ленту.
 */
export function periodFromParams(params: URLSearchParams, now: Date): Period | null {
  if (params.get('period') === 'all') {
    return null;
  }

  const from = parseDay(params.get('from'));
  const to = parseDay(params.get('to')) ?? from;

  if (from === null || to === null) {
    return presetPeriod('today', now);
  }

  return from <= to ? { from, to } : { from: to, to: from };
}

/** Параметры адреса для периода; «сегодня» — без параметров. */
export function periodToParams(
  period: Period | null,
  now: Date,
): Record<'period' | 'from' | 'to', string | undefined> {
  if (period === null) {
    return { period: 'all', from: undefined, to: undefined };
  }

  if (presetOf(period, now) === 'today') {
    return { period: undefined, from: undefined, to: undefined };
  }

  return { period: undefined, from: dayKey(period.from), to: dayKey(period.to) };
}

/** Фильтр запроса: полуинтервал в UTC. */
export function periodFilters(period: Period | null): {
  created_from?: string;
  created_to?: string;
} {
  return period === null
    ? {}
    : {
        created_from: startOfDay(period.from).toISOString(),
        created_to: addDays(period.to, 1).toISOString(),
      };
}

export function periodDays(period: Period): number {
  let days = 1;

  for (let day = period.from; !sameDay(day, period.to); day = addDays(day, 1)) {
    days += 1;
  }

  return days;
}

/** Сетка месяца: шесть недель с понедельника, соседние месяцы — по краям. */
export function monthGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = addDays(first, -((first.getDay() + 6) % 7));

  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}
